import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// The default export of hooks/useAxiosAuth is a FACTORY that builds a fresh
// axios instance, not a React hook — it calls nothing from React. Imported
// under its own name it trips eslint react-hooks/rules-of-hooks, because the
// rule reads `useX()` inside a non-component (a thunk body) as a hook called
// outside a render. Renaming it at the import is the whole fix; the other
// slices predate the rule being enforced.
import createAuthedAxios from "../hooks/useAxiosAuth";

// The pharmacy module, in one slice: the counters and what stands on their
// shelves (master data), the queue of prescriptions waiting to be handed over
// (dispensing), and the bills that come out the other end (billing). Three
// routers on the backend — /api/pharmacy is one mount point shared by
// pharmacyMasterRoutes, pharmacyDispenseRoutes and pharmacyBillingRoutes — and
// one slice here, because it is one person's screen: the pharmacist who picks
// the strip is the pharmacist who prints the bill and takes the cash.
//
// TENANCY. Every endpoint answers a question about ONE hospital's shelves.
// A tenant admin, doctor or employee carries their hospital in the verified
// token and cannot name another, so the `tenantId` these thunks accept is
// ignored for them; Super Admin is deliberately unscoped everywhere in this
// codebase and MUST name the organisation or the request comes back 400. Passing
// it through unconditionally is what lets one screen serve both, instead of the
// pages growing a second code path that only Super Admin ever exercises.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

/**
 * A rejection the SCREEN can act on, not merely display.
 *
 * A pharmacy failure is rarely "something went wrong": it is "there are four
 * left, not six" (409 INSUFFICIENT_STOCK, with the available quantity attached),
 * "this needs a prescription" (403 PRESCRIPTION_REQUIRED), "you may not discount"
 * (403 with the permission id), or "the sale is fine but the PDF is not" (503
 * PDF_UNAVAILABLE). Each of those has a different next action at the counter,
 * and a bare message string forces the pharmacist to work it out from prose.
 * Carrying `code` and `data` costs nothing and is a superset of what the rest of
 * the app reads, so `action.payload?.error` still behaves everywhere.
 */
const rejectionDetail = (error) => ({
    error: getSerializableError(error),
    code: error?.response?.data?.code || null,
    permission: error?.response?.data?.permission || null,
    data: error?.response?.data?.data || null,
});

// Empty values are dropped rather than sent as `?storeId=`. The controllers
// guard with truthiness so an empty string is harmless today, but `?status=` on
// a filtered list is one refactor away from meaning "status equals the empty
// string" and returning nothing, which reads on screen as "this counter has no
// stock" — a wrong answer delivered confidently.
const query = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        qs.append(key, Array.isArray(value) ? value.join(',') : String(value));
    });
    const s = qs.toString();
    return s ? `?${s}` : '';
};

// =========================================================== stores ========

/** The licensed dispensing premises, each with its batch count and stock value. */
export const getStores = createAsyncThunk(
    'pharmacy/getStores',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { status = '', type = '', tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/stores${query({ status, type, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const getStore = createAsyncThunk(
    'pharmacy/getStore',
    async (storeId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/stores/${storeId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const createStore = createAsyncThunk(
    'pharmacy/createStore',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/stores`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const updateStore = createAsyncThunk(
    'pharmacy/updateStore',
    async ({ storeId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/pharmacy/stores/${storeId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// DELETE deactivates; it never removes the row. Invoices, batches and ledger
// entries point at a counter for years after it shuts, and the reply names
// whichever store was promoted to default in its place.
export const deactivateStore = createAsyncThunk(
    'pharmacy/deactivateStore',
    async (storeId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/pharmacy/stores/${storeId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ======================================================== locations ========

export const getLocations = createAsyncThunk(
    'pharmacy/getLocations',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { storeId = '', type = '', status = '', rackName = '', search = '', page = 1, limit = 50, tenantId = '' } = params;
            const response = await axiosInstance.get(
                `/pharmacy/locations${query({ storeId, type, status, rackName, search, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The rack grid — one store's bins laid out as the room is.
 *
 * Kept apart from the paginated list because it is a different question. The
 * list is "find me the bin I am editing"; the map is "walk me round the room",
 * and it comes back grouped into racks with occupancy and nearest expiry per
 * bin. Paginating a floor plan would cut the room in half.
 */
export const getLocationMap = createAsyncThunk(
    'pharmacy/getLocationMap',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { storeId = '', tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/locations/map${query({ storeId, tenantId })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const createLocation = createAsyncThunk(
    'pharmacy/createLocation',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/locations`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const updateLocation = createAsyncThunk(
    'pharmacy/updateLocation',
    async ({ locationId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/pharmacy/locations/${locationId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const deactivateLocation = createAsyncThunk(
    'pharmacy/deactivateLocation',
    async (locationId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/pharmacy/locations/${locationId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ======================================================== medicines ========

export const getMedicines = createAsyncThunk(
    'pharmacy/getMedicines',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { search = '', form = '', schedule = '', locationId = '', isActive = '', page = 1, limit = 25, tenantId = '' } = params;
            const response = await axiosInstance.get(
                `/pharmacy/medicines${query({ search, form, schedule, locationId, isActive, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The counter's type-ahead. Ranked barcode-exact, then prefix, then infix, and
 * every row already carries what the pharmacist needs to decide: the bin to walk
 * to, the FEFO batch with its MRP and expiry, whether any of it is actually
 * sellable, and whether the drug is Schedule H/H1/X.
 *
 * The reply names the storeId it searched. It is stored, because when no store
 * was asked for the backend picks the default counter — and a screen that shows
 * stock figures without saying which counter they came from is how somebody
 * promises a strip that is sitting in the other building.
 */
export const searchMedicines = createAsyncThunk(
    'pharmacy/searchMedicines',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { q = '', storeId = '', limit = 20, tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/medicines/search${query({ q, storeId, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** One drug-master row with its home bin and every batch ever booked against it. */
export const getMedicine = createAsyncThunk(
    'pharmacy/getMedicine',
    async (medicineId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/medicines/${medicineId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const createMedicine = createAsyncThunk(
    'pharmacy/createMedicine',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/medicines`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Note for the form: PUT will not accept isActive=false. Discontinuing goes
// through deactivateMedicine, which refuses while sellable stock is still on the
// shelf — deactivating does not stop FEFO from dispensing it, so the check is
// the only thing that keeps stock from being stranded.
export const updateMedicine = createAsyncThunk(
    'pharmacy/updateMedicine',
    async ({ medicineId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/pharmacy/medicines/${medicineId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

export const deactivateMedicine = createAsyncThunk(
    'pharmacy/deactivateMedicine',
    async (medicineId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.delete(`/pharmacy/medicines/${medicineId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================== batches ========

// Returned in FEFO order, so the list reads in the order the shelf will empty.
// Emptied batches are hidden by default and kept forever behind
// `includeEmpty=true` — a recall notice asks about stock that has already gone.
export const getBatches = createAsyncThunk(
    'pharmacy/getBatches',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { storeId = '', medicineId = '', batchNo = '', status = '', includeEmpty = '', page = 1, limit = 25, tenantId = '' } = params;
            const response = await axiosInstance.get(
                `/pharmacy/batches${query({ storeId, medicineId, batchNo, status, includeEmpty, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Book stock in. Receiving the same batch number twice tops up the existing row
 * rather than creating a second one, so the caller does not have to look first.
 *
 * A 409 BATCH_RACE means two counters booked the same lot in the same instant
 * and the loser must simply send it again — the retry finds the row and adds to
 * it. That code is on the rejection payload; the screen should resubmit rather
 * than tell the storekeeper the goods-in failed.
 */
export const createBatch = createAsyncThunk(
    'pharmacy/createBatch',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/batches`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// Expiry, MRP, purchase rate, bin and status only. Quantity is deliberately
// refused here: stock moves through adjustBatch so that every change leaves a
// ledger row saying who moved it and why.
export const updateBatch = createAsyncThunk(
    'pharmacy/updateBatch',
    async ({ batchId, data }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.put(`/pharmacy/batches/${batchId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * A stock count or a written-down correction.
 *
 * Send EXACTLY ONE of `countedQty` (what was physically counted — the backend
 * works out the difference) or `qtyDelta` (the signed correction). Sending both
 * or neither is refused, because "12" is ambiguous between the two and guessing
 * wrong writes a ledger row that nobody can reconcile against the shelf.
 * `reason` is mandatory: an adjustment is the only stock movement with no
 * document behind it.
 */
export const adjustBatch = createAsyncThunk(
    'pharmacy/adjustBatch',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/batches/adjust`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ===================================================== stock views =========

/** The reorder list: everything at or below its reorder level, out-of-stock first. */
export const getLowStock = createAsyncThunk(
    'pharmacy/getLowStock',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { storeId = '', page = 1, limit = 25, tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/stock/low${query({ storeId, page, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * What is about to expire — and, at the top, what already has.
 *
 * `days` is a look-ahead window, not a start date: already-expired batches with
 * a quantity still on them are the most urgent rows a pharmacy can be shown,
 * because they are unsellable, still in the valuation, and physically sitting
 * where somebody could hand one over.
 */
export const getExpiringStock = createAsyncThunk(
    'pharmacy/getExpiringStock',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { storeId = '', days = 90, page = 1, limit = 25, tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/stock/expiring${query({ storeId, days, page, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ======================================================== dispensing =======

/**
 * The prescriptions waiting at the counter, newest first, fully dispensed ones
 * dropped. Defaults to today; passing `search` widens the scan to the last
 * thirty days, because a patient who comes back for the rest of their course is
 * quoting a name, not a date.
 */
export const getQueue = createAsyncThunk(
    'pharmacy/getQueue',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { search = '', date = '', page = 1, limit = 20, tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/queue${query({ search, date, page, limit, tenantId })}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The picking list for one appointment: every prescribed line matched against
 * the drug master, its bin, the FEFO batch to take, what is on hand, and how
 * much of it has already gone home with the patient.
 *
 * Lines come back sorted by pick sequence rather than in the order the doctor
 * typed them — the order on the prescription means nothing to the shelves, and
 * walking the room once beats crossing it six times. A line whose medicine is
 * null matched nothing in the master and needs a substitute or a new product.
 */
export const getPrescription = createAsyncThunk(
    'pharmacy/getPrescription',
    async ({ appointmentId, storeId = '', tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/prescription/${appointmentId}${query({ storeId, tenantId })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The sale. One call takes the stock off the shelf, writes the ledger, raises
 * the GST invoice and records any tender — all inside one transaction, so a
 * failure anywhere leaves the shelf and the bill both untouched.
 *
 * Body: { storeId, appointmentId?, orderId?, patientId?, walkInName?, walkInPhone?,
 *         patientType: 'OP' | 'IP', isInterState?, placeOfSupply?,
 *         lines: [{ medicineId, qtyDispensed, qtyPrescribed?, batchId?,
 *                   discountPercent?, discountAmount?, substitutedFromMedicineId? }],
 *         payments: [{ method, amount, reference? }] }
 *
 * WHAT THE SCREEN MUST HANDLE, because none of it is a generic error:
 *   409 INSUFFICIENT_STOCK — payload.data carries medicineId, available and
 *       requested. "Out of stock" alone is unactionable; how many there ARE is
 *       what decides whether a partial goes home with the patient.
 *   403 PRESCRIPTION_REQUIRED / PRESCRIBER_REQUIRED — a Schedule H/H1/X drug
 *       cannot leave without an appointmentId or orderId behind it and a named
 *       prescriber for the register.
 *   403 PERMISSION_DENIED — payload.permission names which right was missing.
 *       Discounting and substituting are separate grants checked against the
 *       BODY, so an ordinary dispense never trips them.
 * Omitting `payments` is normal and correct: a ward supply goes on the admission
 * account and an insurance case settles later, so the bill is simply raised
 * unpaid and collected against with collectPayment.
 */
export const dispense = createAsyncThunk(
    'pharmacy/dispense',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/dispense`, payload);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Taking the goods back. Whole dispense only — a partial return is refused with
 * PARTIAL_RETURN_UNSUPPORTED, and the counter reverses the lot and dispenses the
 * balance again. The bill is never deleted: the reply carries a CREDIT NOTE,
 * which is what keeps the GST series consecutive.
 */
export const returnDispense = createAsyncThunk(
    'pharmacy/returnDispense',
    async ({ dispenseId, reason = null }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/dispense/${dispenseId}/return`, { reason });
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** One completed handover, its lines, and every bill raised against it —
 *  the sale and any credit note, each with a freshly signed PDF link. */
export const getDispense = createAsyncThunk(
    'pharmacy/getDispense',
    async (dispenseId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/dispense/${dispenseId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================== billing ========

export const getInvoices = createAsyncThunk(
    'pharmacy/getInvoices',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                search = '', status = '', invoiceType = '', patientId = '', invoiceNo = '',
                storeId = '', from = '', to = '', page = 1, limit = 20, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/pharmacy/invoices${query({ search, status, invoiceType, patientId, invoiceNo, storeId, from, to, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/** One bill with its lines, its tenders, and a `settlement` block giving what is
 *  still owed — derived from the same tender rows the payment route writes, so
 *  the two numbers on the settlement screen can never disagree. */
export const getInvoice = createAsyncThunk(
    'pharmacy/getInvoice',
    async (invoiceId, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/invoices/${invoiceId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * A signed link to the bill PDF.
 *
 * The cached copy is the document AS ISSUED, which is normally what should be
 * handed over — a cancellation does not rewrite a bill, it raises a credit note.
 * `regenerate: true` is the escape hatch for a copy carrying the CANCELLED
 * watermark or a DUPLICATE for an insurer.
 *
 * A 503 PDF_UNAVAILABLE means the BILL IS VALID and only the drawing failed. The
 * screen must say exactly that, or the counter assumes the sale did not happen
 * and rings it up a second time.
 */
export const getInvoicePdf = createAsyncThunk(
    'pharmacy/getInvoicePdf',
    async ({ invoiceId, regenerate = false }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/pharmacy/invoices/${invoiceId}/pdf${query({ regenerate: regenerate ? 'true' : '' })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Re-deliver a bill by email, SMS or WhatsApp. It re-sends the SAME document and
 * never re-issues one — a second number for one sale would break the consecutive
 * GST series. `recipient` is optional and wins when given, which is the common
 * case of a patient whose email was never captured reading it out at the
 * counter; without one on file the call comes back NO_RECIPIENT asking for it.
 */
export const resendInvoice = createAsyncThunk(
    'pharmacy/resendInvoice',
    async ({ invoiceId, channel = 'email', recipient = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/invoices/${invoiceId}/resend`, { channel, recipient });
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Money against a bill. Methods: cash, upi, card, insurance, credit.
 *
 * Part payments are normal — the reply's `settlement` block carries the running
 * balance and the bill's new status, so the screen shows what is still owed from
 * the same source the server just wrote rather than subtracting locally and
 * drifting.
 */
export const collectPayment = createAsyncThunk(
    'pharmacy/collectPayment',
    async ({ invoiceId, method, amount, reference = null, receivedAt = null }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/pharmacy/invoices/${invoiceId}/payment`, {
                method, amount, reference, receivedAt,
            });
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The day's (or period's) takings: sales, credit notes, the net of the two, the
 * GST breakup by rate, and collections by tender.
 *
 * Credit notes come back as their own positive bucket rather than as negative
 * sales, because that is how the GST return treats them — a credit note is a
 * document that REDUCES the period's outward supply, and netting it into the
 * sales figure loses the number the return actually asks for.
 */
export const getSalesReport = createAsyncThunk(
    'pharmacy/getSalesReport',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { from = '', to = '', storeId = '', tenantId = '' } = params;
            const response = await axiosInstance.get(`/pharmacy/reports/sales${query({ from, to, storeId, tenantId })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The writes, listed rather than pattern-matched.
 *
 * staffSlice tests the action type against a regex, which works there because no
 * read is named after a write. Here `getDispense` contains "dispense" and
 * `getInvoicePdf` contains no verb at all, so /dispense|create|update/ would put
 * the whole screen behind a saving spinner every time a pharmacist merely opened
 * a completed handover. Naming the thunks is the version that cannot go wrong
 * when the next one is added.
 */
const MUTATIONS = [
    createStore, updateStore, deactivateStore,
    createLocation, updateLocation, deactivateLocation,
    createMedicine, updateMedicine, deactivateMedicine,
    createBatch, updateBatch, adjustBatch,
    dispense, returnDispense,
    resendInvoice, collectPayment,
];
const MUTATION_TYPES = new Set(MUTATIONS.map((thunk) => thunk.typePrefix));
const matchMutation = (phase) => (action) => {
    const cut = action.type.lastIndexOf('/');
    return cut > 0 && action.type.slice(cut + 1) === phase && MUTATION_TYPES.has(action.type.slice(0, cut));
};

const pharmacySlice = createSlice({
    name: "pharmacy",
    initialState: {
        // Which counter the user is standing at. Held here rather than in each
        // page's own state because every query in the module takes a storeId,
        // and stock figures from two different counters on one screen is how
        // somebody promises a strip that is sitting in the other building.
        activeStoreId: null,

        stores: [],
        storesLoading: false,
        selectedStore: null,

        locations: [],
        locationsTotal: 0,
        locationsTotalPages: 1,
        locationsLoading: false,
        rackMap: null,
        rackMapLoading: false,

        medicines: [],
        medicinesTotal: 0,
        medicinesTotalPages: 1,
        medicinesLoading: false,
        selectedMedicine: null,
        searchResults: [],
        searchStoreId: null,
        searchLoading: false,

        batches: [],
        batchesTotal: 0,
        batchesTotalPages: 1,
        batchesLoading: false,

        queue: [],
        queueTotal: 0,
        queueTotalPages: 1,
        queueDate: null,
        // The scan is capped, so a hospital with a very long day can have rows
        // it never saw. Surfaced so the screen can say "narrow the search"
        // instead of quietly showing a short queue.
        queueTruncated: false,
        queueLoading: false,

        prescription: null,
        prescriptionLoading: false,

        lastDispense: null,
        lastReturn: null,
        // Kept apart from `error`: a refused dispense is a structured answer the
        // counter acts on (how many are left, which permission was missing),
        // not a banner to dismiss. See rejectionDetail.
        dispenseError: null,

        selectedDispense: null,
        dispenseLoading: false,

        invoices: [],
        invoicesTotal: 0,
        invoicesTotalPages: 1,
        invoicesLoading: false,
        selectedInvoice: null,
        invoiceLoading: false,
        // Short-lived by design: the bucket is private and the URL is presigned,
        // so this is a handle for the print button now, never something to
        // persist or share.
        invoicePdf: null,

        lowStock: [],
        lowStockTotal: 0,
        lowStockTotalPages: 1,
        lowStockLoading: false,

        expiring: [],
        expiringTotal: 0,
        expiringTotalPages: 1,
        expiringValueAtRisk: 0,
        expiringLoading: false,

        salesReport: null,
        salesReportLoading: false,

        mutating: false,
        error: null,
    },
    reducers: {
        setActiveStore: (state, action) => { state.activeStoreId = action.payload || null; },
        // Between two patients at the counter. The masters (stores, medicines,
        // bins) survive on purpose — they are the same shelves for the next
        // person, and refetching them makes the screen blink for no reason.
        resetCounter: (state) => {
            state.prescription = null;
            state.searchResults = [];
            state.lastDispense = null;
            state.lastReturn = null;
            state.dispenseError = null;
            state.selectedDispense = null;
            state.invoicePdf = null;
        },
        resetPharmacyState: (state) => {
            state.stores = [];
            state.locations = [];
            state.rackMap = null;
            state.medicines = [];
            state.batches = [];
            state.queue = [];
            state.prescription = null;
            state.invoices = [];
            state.selectedInvoice = null;
            state.selectedStore = null;
            state.selectedMedicine = null;
            state.selectedDispense = null;
            state.lowStock = [];
            state.expiring = [];
            state.salesReport = null;
            state.error = null;
            state.dispenseError = null;
        },
        clearPharmacyError: (state) => { state.error = null; },
        clearDispenseError: (state) => { state.dispenseError = null; },
    },
    extraReducers: (builder) => {
        builder
            // ---- stores ----
            .addCase(getStores.pending, (state) => { state.storesLoading = true; state.error = null; })
            .addCase(getStores.fulfilled, (state, action) => {
                state.storesLoading = false;
                state.stores = action.payload?.data || [];
                // First load lands the user at the default counter rather than
                // at nothing, which is where every single-counter clinic — most
                // of them — should already be.
                if (!state.activeStoreId && state.stores.length) {
                    const preferred = state.stores.find((s) => s.isDefault && s.status === 'active')
                        || state.stores.find((s) => s.status === 'active');
                    state.activeStoreId = preferred?.storeId || null;
                }
            })
            .addCase(getStores.rejected, (state, action) => {
                state.storesLoading = false;
                state.error = action.payload?.error || 'Failed to load pharmacy counters';
            })
            .addCase(getStore.fulfilled, (state, action) => { state.selectedStore = action.payload; })

            // ---- locations ----
            .addCase(getLocations.pending, (state) => { state.locationsLoading = true; state.error = null; })
            .addCase(getLocations.fulfilled, (state, action) => {
                state.locationsLoading = false;
                state.locations = action.payload?.data || [];
                state.locationsTotal = action.payload?.total || 0;
                state.locationsTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getLocations.rejected, (state, action) => {
                state.locationsLoading = false;
                state.error = action.payload?.error || 'Failed to load rack locations';
            })

            .addCase(getLocationMap.pending, (state) => { state.rackMapLoading = true; })
            .addCase(getLocationMap.fulfilled, (state, action) => {
                state.rackMapLoading = false;
                state.rackMap = action.payload || null;
            })
            .addCase(getLocationMap.rejected, (state, action) => {
                state.rackMapLoading = false;
                state.error = action.payload?.error || 'Failed to load the rack map';
            })

            // ---- medicines ----
            .addCase(getMedicines.pending, (state) => { state.medicinesLoading = true; state.error = null; })
            .addCase(getMedicines.fulfilled, (state, action) => {
                state.medicinesLoading = false;
                state.medicines = action.payload?.data || [];
                state.medicinesTotal = action.payload?.total || 0;
                state.medicinesTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getMedicines.rejected, (state, action) => {
                state.medicinesLoading = false;
                state.error = action.payload?.error || 'Failed to load the drug master';
            })

            .addCase(searchMedicines.pending, (state) => { state.searchLoading = true; })
            .addCase(searchMedicines.fulfilled, (state, action) => {
                state.searchLoading = false;
                state.searchResults = action.payload?.data || [];
                state.searchStoreId = action.payload?.storeId || null;
            })
            // A failed type-ahead never raises the page banner. The pharmacist is
            // mid-keystroke; the next one supersedes it, and a toast per
            // character is noise that trains people to ignore the real ones.
            .addCase(searchMedicines.rejected, (state) => { state.searchLoading = false; })

            .addCase(getMedicine.fulfilled, (state, action) => { state.selectedMedicine = action.payload; })

            // ---- batches ----
            .addCase(getBatches.pending, (state) => { state.batchesLoading = true; state.error = null; })
            .addCase(getBatches.fulfilled, (state, action) => {
                state.batchesLoading = false;
                state.batches = action.payload?.data || [];
                state.batchesTotal = action.payload?.total || 0;
                state.batchesTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getBatches.rejected, (state, action) => {
                state.batchesLoading = false;
                state.error = action.payload?.error || 'Failed to load batches';
            })

            // ---- stock views ----
            .addCase(getLowStock.pending, (state) => { state.lowStockLoading = true; })
            .addCase(getLowStock.fulfilled, (state, action) => {
                state.lowStockLoading = false;
                state.lowStock = action.payload?.data || [];
                state.lowStockTotal = action.payload?.total || 0;
                state.lowStockTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getLowStock.rejected, (state, action) => {
                state.lowStockLoading = false;
                state.error = action.payload?.error || 'Failed to load the reorder list';
            })

            .addCase(getExpiringStock.pending, (state) => { state.expiringLoading = true; })
            .addCase(getExpiringStock.fulfilled, (state, action) => {
                state.expiringLoading = false;
                state.expiring = action.payload?.data || [];
                state.expiringTotal = action.payload?.total || 0;
                state.expiringTotalPages = action.payload?.totalPages || 1;
                state.expiringValueAtRisk = action.payload?.valueAtRisk || 0;
            })
            .addCase(getExpiringStock.rejected, (state, action) => {
                state.expiringLoading = false;
                state.error = action.payload?.error || 'Failed to load the expiry report';
            })

            // ---- dispensing ----
            .addCase(getQueue.pending, (state) => { state.queueLoading = true; state.error = null; })
            .addCase(getQueue.fulfilled, (state, action) => {
                state.queueLoading = false;
                state.queue = action.payload?.data || [];
                state.queueTotal = action.payload?.total || 0;
                state.queueTotalPages = action.payload?.totalPages || 1;
                state.queueDate = action.payload?.date || null;
                state.queueTruncated = !!action.payload?.truncated;
            })
            .addCase(getQueue.rejected, (state, action) => {
                state.queueLoading = false;
                state.error = action.payload?.error || 'Failed to load the dispensing queue';
            })

            .addCase(getPrescription.pending, (state) => {
                state.prescriptionLoading = true;
                // The previous patient's lines are dropped before the next
                // patient's arrive. Leaving them up means a pharmacist can pick
                // against a list that belongs to somebody else for as long as
                // the request takes.
                state.prescription = null;
                state.dispenseError = null;
                state.error = null;
            })
            .addCase(getPrescription.fulfilled, (state, action) => {
                state.prescriptionLoading = false;
                state.prescription = action.payload || null;
            })
            .addCase(getPrescription.rejected, (state, action) => {
                state.prescriptionLoading = false;
                state.error = action.payload?.error || 'Failed to load the prescription';
            })

            .addCase(dispense.pending, (state) => { state.dispenseError = null; })
            .addCase(dispense.fulfilled, (state, action) => {
                state.lastDispense = action.payload || null;
                // The bill exists now, so the print button has something to
                // point at without a second round trip.
                state.selectedInvoice = action.payload?.invoice || null;
            })
            .addCase(dispense.rejected, (state, action) => { state.dispenseError = action.payload || null; })

            .addCase(returnDispense.fulfilled, (state, action) => { state.lastReturn = action.payload || null; })
            .addCase(returnDispense.rejected, (state, action) => { state.dispenseError = action.payload || null; })

            .addCase(getDispense.pending, (state) => { state.dispenseLoading = true; })
            .addCase(getDispense.fulfilled, (state, action) => {
                state.dispenseLoading = false;
                state.selectedDispense = action.payload || null;
            })
            .addCase(getDispense.rejected, (state, action) => {
                state.dispenseLoading = false;
                state.error = action.payload?.error || 'Failed to load the dispense';
            })

            // ---- billing ----
            .addCase(getInvoices.pending, (state) => { state.invoicesLoading = true; state.error = null; })
            .addCase(getInvoices.fulfilled, (state, action) => {
                state.invoicesLoading = false;
                state.invoices = action.payload?.data || [];
                state.invoicesTotal = action.payload?.total || 0;
                state.invoicesTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.error = action.payload?.error || 'Failed to load invoices';
            })

            .addCase(getInvoice.pending, (state) => { state.invoiceLoading = true; })
            .addCase(getInvoice.fulfilled, (state, action) => {
                state.invoiceLoading = false;
                state.selectedInvoice = action.payload || null;
            })
            .addCase(getInvoice.rejected, (state, action) => {
                state.invoiceLoading = false;
                state.error = action.payload?.error || 'Failed to load the invoice';
            })

            .addCase(getInvoicePdf.pending, (state) => { state.invoicePdf = null; })
            .addCase(getInvoicePdf.fulfilled, (state, action) => { state.invoicePdf = action.payload || null; })
            .addCase(getInvoicePdf.rejected, (state, action) => {
                // PDF_UNAVAILABLE is not a failed sale. The message the backend
                // sends already says the bill is valid, so it is passed through
                // rather than replaced with a house string that would not.
                state.error = action.payload?.error || 'Failed to produce the bill PDF';
            })

            .addCase(collectPayment.fulfilled, (state, action) => {
                // The server's settled totals replace what is on screen, rather
                // than the screen subtracting the tender itself. Two cashiers
                // taking part payments on one bill would otherwise each show a
                // balance that is right only from where they are standing.
                if (action.payload?.invoice) {
                    state.selectedInvoice = {
                        ...(state.selectedInvoice || {}),
                        ...action.payload.invoice,
                        settlement: action.payload.settlement || null,
                    };
                }
            })

            .addCase(getSalesReport.pending, (state) => { state.salesReportLoading = true; state.error = null; })
            .addCase(getSalesReport.fulfilled, (state, action) => {
                state.salesReportLoading = false;
                state.salesReport = action.payload || null;
            })
            .addCase(getSalesReport.rejected, (state, action) => {
                state.salesReportLoading = false;
                state.error = action.payload?.error || 'Failed to build the sales report';
            })

            // Every mutation shares one flag; the pages refetch on success.
            .addMatcher(matchMutation('pending'), (state) => { state.mutating = true; state.error = null; })
            .addMatcher(matchMutation('fulfilled'), (state) => { state.mutating = false; })
            .addMatcher(matchMutation('rejected'), (state, action) => {
                state.mutating = false;
                state.error = action.payload?.error || 'Action failed';
            });
    },
});

export const {
    setActiveStore,
    resetCounter,
    resetPharmacyState,
    clearPharmacyError,
    clearDispenseError,
} = pharmacySlice.actions;
export default pharmacySlice.reducer;
